/**
 * Average bond energies (bond enthalpies), kJ/mol, for gaseous molecules.
 *
 * Source: OpenStax, *Chemistry 2e*, section 7.5 "Strengths of Ionic and Covalent Bonds",
 * Table 7.2 "Bond Energies (kJ/mol)" (https://openstax.org/books/chemistry-2e/pages/7-5-strengths-of-ionic-and-covalent-bonds),
 * transcribed here and checked value by value against the page twice. Each value is the
 * average energy to break that bond in the gas phase, taken over many compounds, so it is
 * an estimate for any one molecule: the C=O in CO₂ (about 799 kJ/mol) is stronger than the
 * 741 kJ/mol average, for example.
 *
 * Rows are [atom, atom, order, kJ/mol] in the order the table prints them.
 */

export type BondOrder = 1 | 2 | 3;

type BondRow = readonly [string, string, BondOrder, number];

const ROWS: readonly BondRow[] = [
  ["H", "H", 1, 436],
  ["H", "C", 1, 415],
  ["H", "N", 1, 390],
  ["H", "O", 1, 464],
  ["H", "F", 1, 569],
  ["H", "Si", 1, 395],
  ["H", "P", 1, 320],
  ["H", "S", 1, 340],
  ["H", "Cl", 1, 432],
  ["H", "Br", 1, 370],
  ["H", "I", 1, 295],
  ["C", "C", 1, 345],
  ["C", "C", 2, 611],
  ["C", "C", 3, 837],
  ["C", "N", 1, 290],
  ["C", "N", 2, 615],
  ["C", "N", 3, 891],
  ["C", "O", 1, 350],
  ["C", "O", 2, 741],
  ["C", "O", 3, 1080],
  ["C", "F", 1, 439],
  ["C", "Si", 1, 360],
  ["C", "P", 1, 265],
  ["C", "S", 1, 260],
  ["C", "Cl", 1, 330],
  ["C", "Br", 1, 275],
  ["C", "I", 1, 240],
  ["N", "N", 1, 160],
  ["N", "N", 2, 418],
  ["N", "N", 3, 946],
  ["N", "O", 1, 200],
  ["N", "F", 1, 270],
  ["N", "P", 1, 210],
  ["N", "Cl", 1, 200],
  ["N", "Br", 1, 245],
  ["O", "O", 1, 140],
  ["O", "O", 2, 498],
  ["O", "F", 1, 160],
  ["O", "Si", 1, 370],
  ["O", "P", 1, 350],
  ["O", "Cl", 1, 205],
  ["O", "I", 1, 200],
  ["F", "F", 1, 160],
  ["F", "Si", 1, 540],
  ["F", "P", 1, 489],
  ["F", "S", 1, 285],
  ["F", "Cl", 1, 255],
  ["F", "Br", 1, 235],
  ["Si", "Si", 1, 230],
  ["Si", "P", 1, 215],
  ["Si", "S", 1, 225],
  ["Si", "Cl", 1, 359],
  ["Si", "Br", 1, 290],
  ["Si", "I", 1, 215],
  ["P", "P", 1, 215],
  ["P", "S", 1, 230],
  ["P", "Cl", 1, 330],
  ["P", "Br", 1, 270],
  ["P", "I", 1, 215],
  ["S", "S", 1, 215],
  ["S", "Cl", 1, 250],
  ["S", "Br", 1, 215],
  ["Cl", "Cl", 1, 243],
  ["Cl", "Br", 1, 220],
  ["Cl", "I", 1, 210],
  ["Br", "Br", 1, 190],
  ["Br", "I", 1, 180],
  ["I", "I", 1, 150],
];

export interface Bond {
  a: string;
  b: string;
  order: BondOrder;
  /** Average bond energy, kJ/mol. */
  energy: number;
  /** "C–H", "C=O", "C≡N". */
  label: string;
}

const ORDER_SYMBOL: Record<BondOrder, string> = { 1: "–", 2: "=", 3: "≡" };

export const BONDS: readonly Bond[] = ROWS.map(([a, b, order, energy]) => ({
  a,
  b,
  order,
  energy,
  label: `${a}${ORDER_SYMBOL[order]}${b}`,
}));

/** Key independent of the order the atoms are typed in: "C-H" and "H-C" are one bond. */
export function bondKey(a: string, b: string, order: number): string {
  return [a, b].sort().join("|") + order;
}

const BY_KEY = new Map(
  BONDS.map((bond) => [bondKey(bond.a, bond.b, bond.order), bond])
);

export function findBond(
  a: string,
  b: string,
  order: number
): Bond | undefined {
  return BY_KEY.get(bondKey(a, b, order));
}

export const SOURCE_NAME = "OpenStax Chemistry 2e, Table 7.2";
